import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type ComponentProps, type MouseEvent, type ReactNode, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import PageDataState from '@/components/PageDataState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import {
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
  tableInnerHeight,
} from '@/theme/responsive'

import {
  filterPurchaseReferenceRows,
  formatPurchaseReferenceValue,
  type PurchaseReferencePageConfig,
} from './purchaseReferenceConfig'
import type { PurchaseContractColumnsMode } from '@/store/api/purchaseContractApi'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'

type MuiTableProps = ComponentProps<typeof Table>
type MuiTableHeadProps = ComponentProps<typeof TableHead>
type MuiTableBodyProps = ComponentProps<typeof TableBody>
type MuiTableRowProps = ComponentProps<typeof TableRow>

interface PurchaseReferenceGridProps {
  page: PurchaseReferencePageConfig
  columnsMode: PurchaseContractColumnsMode
  rows: Record<string, unknown>[]
  search: string
  onSearchChange: (value: string) => void
  loading?: boolean
  error?: unknown
  onRetry?: () => void
  /** Bảng FILL hết chiều cao parent (bỏ cap 620) — CHỈ bật khi parent bounded
      (vd PurchaseReferencesView trong Card). Default false = giữ height cố định an toàn. */
  fillHeight?: boolean
  /** Nội dung chèn vào FilterBar (sau nút Search, trước Columns) — vd nút Back
      "Datasets" + accent pill khi nhúng trong Factory Data hub. */
  headerSlot?: ReactNode
}

export default function PurchaseReferenceGrid({
  page,
  columnsMode,
  rows,
  search,
  onSearchChange,
  loading = false,
  error,
  onRetry,
  fillHeight = false,
  headerSlot,
}: PurchaseReferenceGridProps) {
  const [pageIndex, setPageIndex] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filterPopover = useFilterPopover<'search'>()
  const closeFilterPopover = () => filterPopover.close()

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const { getWidth, startResize } = useColumnWidths('purchase.references')

  const allColumns = useMemo(
    () => page.columns.map((c) => ({ key: c.field, label: c.headerName })),
    [page.columns],
  )
  const colByField = useMemo(() => new Map(page.columns.map((c) => [c.field, c])), [page.columns])
  const columnsCtrl = usePreferenceColumns({
    scope: 'purchase',
    prefKey: 'columns',
    subKey: columnsMode,
    allColumns,
    widthsTableKey: 'purchase.references',
  })
  const { visibleColumns, openMenu } = columnsCtrl

  const searchedRows = useMemo(
    () =>
      filterPurchaseReferenceRows(rows, search, page.columns).map((row, index) => ({
        id: `${page.pageKey}-${index}`,
        ...row,
      }) as Record<string, unknown>),
    [page.pageKey, page.columns, rows, search],
  )

  const filteredRows = useMemo(
    () =>
      columnFilters.hasAny
        ? searchedRows.filter((row) => rowMatchesAllColumnFilters(row, columnFilters.filters))
        : searchedRows,
    [searchedRows, columnFilters.filters, columnFilters.hasAny],
  )

  // Per-column ▽ popover metadata
  const popoverColumn = useMemo(
    () => (colPopover ? colByField.get(colPopover.key) ?? null : null),
    [colPopover, colByField],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => {
      if (!popoverColumn) return 'text'
      const colDef: { key: string; kind?: string } = { key: popoverColumn.field, kind: popoverColumn.kind }
      return inferColumnFilterKind(colDef as never, rows as never)
    },
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(rows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, rows],
  )

  const sortedRows = useMemo(
    () =>
      sortTableRows(
        filteredRows,
        sortState,
        visibleColumns.map((vc) => {
          const c = colByField.get(vc.key)
          return { field: vc.key, kind: c?.kind }
        }),
      ),
    [colByField, filteredRows, sortState, visibleColumns],
  )

  const effectivePageIndex = useMemo(() => {
    if (rowsPerPage === -1) return 0
    const maxPage = Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1)
    return Math.min(pageIndex, maxPage)
  }, [pageIndex, rowsPerPage, sortedRows.length])

  const visibleRows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = effectivePageIndex * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [effectivePageIndex, rowsPerPage, sortedRows])

  const isEmpty = !loading && !error && filteredRows.length === 0
  const minTableWidth = useMemo(
    () =>
      Math.max(
        1200,
        visibleColumns.reduce((total, vc) => {
          const c = colByField.get(vc.key)
          return total + (c?.width ?? c?.minWidth ?? 160)
        }, 0),
      ),
    [colByField, visibleColumns],
  )
  const gridKey = `${page.pageKey}:${rowsPerPage}:${effectivePageIndex}:${filteredRows.length}:${visibleColumns.length}`
  const hasActiveFilters = !!search.trim() || columnFilters.hasAny
  const activeFilterCount = (search.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    onSearchChange('')
    columnFilters.clearAll()
    setRowsPerPage(-1)
    setPageIndex(0)
    closeFilterPopover()
  }
  const toggleSort = (field: string) => {
    setSortState((current) => toggleTableSort(current, field))
    setPageIndex(0)
  }

  const searchBtn = (
    <FilterButton label="Search" active={!!search.trim()} onClick={(e) => filterPopover.open('search', e)} />
  )
  const columnsBtn = (
    <Button
      size="small"
      variant="outlined"
      endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
      onClick={(e: MouseEvent<HTMLElement>) => openMenu(e.currentTarget)}
      sx={filterBarBtnSx}
      title="Columns"
    >
      <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
      <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
    </Button>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minHeight: 0, flex: 1,
               ...(fillHeight ? { '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' } } : {}) }}>
      <Box className="app-dataframe">
        <FilterBar
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
          rightSlot={
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
              {loading && <CircularProgress size={15} />}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Total: {filteredRows.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Loaded: {rows.length}
              </Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={effectivePageIndex}
                onPageChange={(_, newPage) => setPageIndex(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value))
                  setPageIndex(0)
                }}
                rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
                labelRowsPerPage="Rows"
                sx={TABLE_PAGINATION_COMPACT}
              />
            </Stack>
          }
        >
          {/* Thống nhất Data hub: Back+pill (headerSlot) ĐẦU TIÊN, rồi Search,
              rồi Columns — tất cả gói trái 1 FilterBar (Datasets-first). */}
          {headerSlot}
          {searchBtn}
          {columnsBtn}
        </FilterBar>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={360}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={search}
              onChange={(v) => { onSearchChange(v); setPageIndex(0) }}
              placeholder="Filter loaded rows..."
              label="Search pages"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.headerName ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => { if (!colPopover) return; columnFilters.setFilter(colPopover.key, v); setPageIndex(0) }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <PageDataState
          loading={loading}
          error={error}
          empty={isEmpty}
          emptyMessage="No rows (or empty table)."
          onRetry={onRetry}
          minHeight={300}
        >
          <Box sx={fillHeight ? { flex: 1, minHeight: { xs: 280, sm: 320 }, overflow: 'hidden' } : tableInnerHeight(620)}>
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={visibleRows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.flatMap((vc) => {
                    const column = colByField.get(vc.key)
                    if (!column) return []
                    const w = getWidth(column.field)
                    return [
                      <TableCell
                        key={column.field}
                        align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(column.field)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        // userSelect:'text' để bôi đen + copy tên cột (click-sort vẫn chạy).
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', position: 'relative' }}
                      >
                        {column.headerName}
                        {getTableSortSuffix(sortState, column.field)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(column.field)}
                          onClick={(position) => setColPopover({ key: column.field, position })}
                          ariaLabel={`Filter ${column.headerName}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(column.field, e)} />
                      </TableCell>,
                    ]
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: Record<string, unknown>) =>
                visibleColumns.flatMap((vc) => {
                  const column = colByField.get(vc.key)
                  if (!column) return []
                  const value = formatPurchaseReferenceValue(row[column.field], column.kind, column.precision ?? 0)
                  const w = getWidth(column.field)
                  const wrap = getWidth(column.field) != null
                  if (wrap) {
                    return [
                      <TableCell
                        key={column.field}
                        align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {value || '-'}
                        </Box>
                      </TableCell>,
                    ]
                  }
                  return [
                    <TableCell
                      key={column.field}
                      align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={value || '-'}
                        sx={{ display: 'inline-block', width: '100%' }}
                      >
                        {value || '-'}
                      </Box>
                    </TableCell>,
                  ]
                })
              }
              components={{
                Table: (props: MuiTableProps) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: (props: MuiTableHeadProps) => <TableHead {...props} component="thead" />,
                TableBody: (props: MuiTableBodyProps) => <TableBody {...props} component="tbody" />,
                TableRow: (props: MuiTableRowProps) => <TableRow {...props} hover />,
              }}
            />
          </Box>
        </PageDataState>
      </Box>
    </Box>
  )
}
